import { useState } from 'react';
import {
  CheckCircle2,
  XCircle,
  HelpCircle,
  FileText,
  BarChart3,
  AlertTriangle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { useAuthStore } from '@/store/authStore';
import {
  useReviewDecisions,
  useReviewStats,
  useSubmitDecision,
} from '@/features/review/hooks/useReview';
import type {
  Review,
  ReviewDecision,
  ReviewDecisionType,
  ReviewPhase,
} from '@/features/review/types';

// =========================================
// Types
// =========================================

interface SourceItem {
  id: string;
  title: string | null;
  file_name: string;
}

interface ReviewerPanelProps {
  review: Review;
  sources: SourceItem[];
  phase: ReviewPhase;
}

// =========================================
// Decision button config
// =========================================

const DECISION_CONFIG: Record<
  ReviewDecisionType,
  { label: string; icon: typeof CheckCircle2; color: string; bgColor: string }
> = {
  include: {
    label: 'Include',
    icon: CheckCircle2,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50 hover:bg-emerald-100 border-emerald-200',
  },
  exclude: {
    label: 'Exclude',
    icon: XCircle,
    color: 'text-rose-600',
    bgColor: 'bg-rose-50 hover:bg-rose-100 border-rose-200',
  },
  maybe: {
    label: 'Maybe',
    icon: HelpCircle,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50 hover:bg-amber-100 border-amber-200',
  },
};

// =========================================
// ReviewerPanel Component
// =========================================

export function ReviewerPanel({
  review,
  sources,
  phase,
}: ReviewerPanelProps) {
  const user = useAuthStore((s) => s.user);
  const { data: decisions } = useReviewDecisions(review.id, phase);
  const { data: stats } = useReviewStats(review.id, phase);
  const submitDecision = useSubmitDecision();

  const [activeSourceId, setActiveSourceId] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  // Get the current user's decision for a specific source
  const getMyDecision = (sourceId: string): ReviewDecision | undefined => {
    return decisions?.find(
      (d) =>
        d.source_id === sourceId &&
        d.reviewer_id === user?.id &&
        d.phase === phase
    );
  };

  // Get all decisions for a source (visible only if not blinded, or own decisions)
  const getSourceDecisions = (sourceId: string): ReviewDecision[] => {
    if (!decisions) return [];
    return decisions.filter(
      (d) =>
        d.source_id === sourceId &&
        d.phase === phase &&
        (!review.blinded || d.reviewer_id === user?.id)
    );
  };

  const handleDecision = async (
    sourceId: string,
    decision: ReviewDecisionType
  ) => {
    try {
      await submitDecision.mutateAsync({
        review_id: review.id,
        source_id: sourceId,
        decision,
        reason: reason.trim() || undefined,
        phase,
      });
      setReason('');
      setActiveSourceId(null);
      toast.success(`Marked as ${decision}`);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to submit decision'
      );
    }
  };

  // Compute progress
  const reviewedCount = sources.filter((s) => getMyDecision(s.id)).length;
  const progressPercent =
    sources.length > 0 ? (reviewedCount / sources.length) * 100 : 0;

  return (
    <div className="space-y-6">
      {/* Progress overview */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-indigo-600" />
            Your Progress — {phase === 'screening' ? 'Screening' : 'Full Text'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-600">
              {reviewedCount} of {sources.length} reviewed
            </span>
            <span className="font-medium text-slate-900">
              {Math.round(progressPercent)}%
            </span>
          </div>
          <Progress value={progressPercent} className="h-2" />

          {stats && (
            <div className="flex gap-4 pt-2">
              <div className="flex items-center gap-1.5 text-xs">
                <div className="h-2 w-2 rounded-full bg-emerald-500" />
                <span className="text-slate-600">
                  {stats.included} included
                </span>
              </div>
              <div className="flex items-center gap-1.5 text-xs">
                <div className="h-2 w-2 rounded-full bg-rose-500" />
                <span className="text-slate-600">
                  {stats.excluded} excluded
                </span>
              </div>
              <div className="flex items-center gap-1.5 text-xs">
                <div className="h-2 w-2 rounded-full bg-amber-500" />
                <span className="text-slate-600">{stats.maybe} maybe</span>
              </div>
              {stats.conflicts > 0 && (
                <div className="flex items-center gap-1.5 text-xs">
                  <AlertTriangle className="h-3 w-3 text-orange-500" />
                  <span className="text-orange-600">
                    {stats.conflicts - stats.resolved_conflicts} conflicts
                  </span>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Source list with decision buttons */}
      <div className="space-y-3">
        {sources.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-10">
              <FileText className="h-10 w-10 text-slate-300 mb-3" />
              <p className="text-slate-500 text-sm">
                No sources assigned for review
              </p>
            </CardContent>
          </Card>
        ) : (
          sources.map((source) => {
            const myDecision = getMyDecision(source.id);
            const sourceDecisions = getSourceDecisions(source.id);
            const isExpanded = activeSourceId === source.id;

            return (
              <Card
                key={source.id}
                className={cn(
                  'transition-all',
                  myDecision && 'border-l-4',
                  myDecision?.decision === 'include' &&
                    'border-l-emerald-500',
                  myDecision?.decision === 'exclude' &&
                    'border-l-rose-500',
                  myDecision?.decision === 'maybe' && 'border-l-amber-500'
                )}
              >
                <CardContent className="py-3 px-4">
                  <div className="flex items-center justify-between gap-4">
                    {/* Source info */}
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">
                          {source.title ?? source.file_name}
                        </p>
                        {source.title && (
                          <p className="text-xs text-slate-500 truncate">
                            {source.file_name}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Decision badges (others, visible only if unblinded) */}
                    <div className="flex items-center gap-2 shrink-0">
                      {!review.blinded &&
                        sourceDecisions
                          .filter((d) => d.reviewer_id !== user?.id)
                          .map((d) => {
                            const config = DECISION_CONFIG[d.decision];
                            const Icon = config.icon;
                            return (
                              <Tooltip key={d.id}>
                                <TooltipTrigger>
                                  <Icon
                                    className={cn('h-4 w-4', config.color)}
                                  />
                                </TooltipTrigger>
                                <TooltipContent>
                                  {d.reviewer?.full_name ?? d.reviewer?.email}:{' '}
                                  {config.label}
                                </TooltipContent>
                              </Tooltip>
                            );
                          })}

                      {/* Current user's decision */}
                      {myDecision ? (
                        <Badge
                          variant="secondary"
                          className={cn(
                            'text-xs gap-1 cursor-pointer',
                            DECISION_CONFIG[myDecision.decision].color
                          )}
                          onClick={() =>
                            setActiveSourceId(
                              isExpanded ? null : source.id
                            )
                          }
                        >
                          {(() => {
                            const config =
                              DECISION_CONFIG[myDecision.decision];
                            const Icon = config.icon;
                            return (
                              <>
                                <Icon className="h-3 w-3" />
                                {config.label}
                              </>
                            );
                          })()}
                        </Badge>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs h-7"
                          onClick={() =>
                            setActiveSourceId(
                              isExpanded ? null : source.id
                            )
                          }
                        >
                          Review
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Expanded decision area */}
                  {isExpanded && (
                    <div className="mt-4 pt-3 border-t space-y-3">
                      <div className="flex gap-2">
                        {(
                          ['include', 'exclude', 'maybe'] as const
                        ).map((decision) => {
                          const config = DECISION_CONFIG[decision];
                          const Icon = config.icon;
                          const isActive =
                            myDecision?.decision === decision;

                          return (
                            <Button
                              key={decision}
                              size="sm"
                              variant="outline"
                              className={cn(
                                'flex-1 gap-1.5',
                                isActive && config.bgColor
                              )}
                              disabled={submitDecision.isPending}
                              onClick={() =>
                                handleDecision(source.id, decision)
                              }
                            >
                              <Icon className={cn('h-4 w-4', config.color)} />
                              {config.label}
                            </Button>
                          );
                        })}
                      </div>
                      <Textarea
                        placeholder="Reason (optional)..."
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        rows={2}
                        className="text-sm resize-none"
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}
